/**
 * CustomerCheckout — Kredi kartı ödeme formu
 * Stripe (gerçek ödeme) + Mock (test için) iki seçenek
 */
import React, { useState, useEffect } from 'react';
import { X, CreditCard, Lock, CheckCircle, ShoppingBag, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../lib/apiClient';
import { checkout } from '../api/customerApi';

function fmtCard(v)   { return v.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim(); }
function fmtExpiry(v) { const d = v.replace(/\D/g,'').slice(0,4); return d.length>=3 ? d.slice(0,2)+'/'+d.slice(2) : d; }
function fmtCurrency(amount, currency = 'USD') {
  const syms = { USD: '$', EUR: '€', TRY: '₺' };
  return `${syms[currency] || currency} ${Number(amount).toLocaleString('tr-TR', {minimumFractionDigits:0})}`;
}

export default function CustomerCheckout({ cart = [], language, profileData, userInfo, tenantSlug = '', onClose, onSuccess, supplierTenantId = 'quattro-stand' }) {
  const tr      = language === 'tr';
  const total   = cart.reduce((s, i) => s + (Number(i.line_total) || Number(i.unit_price) * (i.quantity||1)), 0);
  const currency = cart[0]?.currency || 'USD';

  const [step, setStep]         = useState('form');  // 'form' | 'processing' | 'done' | 'stripe_loading'
  const [order, setOrder]       = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  const pd = profileData || {};
  const ui = userInfo    || {};

  const [company, setCompany] = useState({
    company_name: pd.company_name || '',
    contact_name: ui.full_name || pd.contact_name || '',
    email:        pd.contact_email || ui.email || '',
    phone:        pd.phone || '',
  });
  useEffect(() => {
    if (!profileData) return;
    setCompany({
      company_name: pd.company_name || '',
      contact_name: ui.full_name || pd.contact_name || '',
      email:        pd.contact_email || ui.email || '',
      phone:        pd.phone || '',
    });
  }, [JSON.stringify(profileData)]);

  // Mock kart (test)
  const [card, setCard] = useState({
    number: '4242 4242 4242 4242',
    expiry: '12/28',
    cvc: '123',
    holder: ui.full_name || pd.contact_name || 'AD SOYAD',
  });
  useEffect(() => {
    if (ui.full_name || pd.contact_name) {
      setCard(c => ({ ...c, holder: ui.full_name || pd.contact_name || c.holder }));
    }
  }, [ui.full_name, pd.contact_name]);

  // ── Stripe Gerçek Ödeme ───────────────────────────────────────────────────
  const handleStripePayment = async () => {
    if (cart.length === 0) {
      toast.error(tr ? 'Sepet boş.' : 'Cart is empty.'); return;
    }
    setStripeLoading(true);
    try {
      const origin_url = window.location.origin;
      const slug = tenantSlug || window.location.pathname.split('/')[2] || '';
      const res = await apiClient.post(
        '/customer/create-stripe-session',
        { origin_url, tenant_slug: slug },
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(tr ? 'Stripe bağlantısı oluşturulamadı.' : 'Failed to create Stripe session.');
        setStripeLoading(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Stripe hatası.' : 'Stripe error.'));
      setStripeLoading(false);
    }
  };

  // ── Mock Ödeme (Test) ─────────────────────────────────────────────────────
  // Used by admins/QA to exercise post-payment flows without real Stripe.
  // Backend immediately marks the order(s) as `payment_status="paid"` when
  // it sees `mock_payment: true`. The supplier still has to confirm.
  const handlePay = async () => {
    if (cart.length === 0) {
      toast.error(tr ? 'Sepet boş.' : 'Cart is empty.');
      return;
    }
    setStep('processing');
    try {
      await new Promise(r => setTimeout(r, 1500));
      const result = await checkout({
        order_type:         'direct',
        // Backend now derives the supplier from cart-item granularity, so
        // this top-level field is just a fallback for items that don't
        // carry their own supplier_tenant_id.
        supplier_tenant_id: supplierTenantId,
        customer_company:   company,
        notes:              '',
        mock_payment:       true,
      });
      setOrder(result);
      setStep('done');
      onSuccess?.(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Ödeme başarısız.' : 'Payment failed.'));
      setStep('form');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-3"
      onClick={step === 'done' ? undefined : onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
        style={{ maxHeight: '95vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── DONE ekranı ── */}
        {step === 'done' && order && (
          <div className="flex flex-col items-center justify-center py-14 px-8 text-center gap-5">
            {/* Animasyonlu tik */}
            <div className="relative">
              <div className="h-24 w-24 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                <CheckCircle className="h-12 w-12 text-white" strokeWidth={2.5} />
              </div>
              <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white flex items-center justify-center shadow-lg">
                <Lock className="h-4 w-4 text-emerald-600" />
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-1">
                {tr ? 'Ödeme Tamamlandı!' : 'Payment Complete!'}
              </h2>
              <p className="text-gray-500 text-sm">
                {tr ? 'Siparişiniz başarıyla alındı.' : 'Your order has been placed successfully.'}
              </p>
            </div>

            {/* Sipariş özeti */}
            <div className="w-full bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{tr ? 'Sipariş No' : 'Order No'}</span>
                <span className="font-bold text-emerald-700 font-mono">{order.order_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{tr ? 'Toplam Tutar' : 'Total Amount'}</span>
                <span className="font-bold text-gray-900">{fmtCurrency(order.total_amount, order.currency || currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{tr ? 'Durum' : 'Status'}</span>
                <span className="font-semibold text-amber-600">{tr ? 'Onay Bekliyor' : 'Pending Confirmation'}</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed">
              {tr
                ? 'Siparişiniz onay sürecindedir. Tedarikçi en kısa sürede sizinle iletişime geçecektir.'
                : 'Your order is under review. The supplier will contact you shortly.'}
            </p>

            <button
              onClick={onClose}
              className="w-full py-3.5 rounded-2xl text-white font-bold text-base transition-all"
              style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
              data-testid="close-success-btn"
            >
              {tr ? 'Siparişleri Görüntüle' : 'View Orders'}
            </button>
          </div>
        )}

        {/* ── İŞLEM YAPILIYOR ── */}
        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center py-16 gap-5 px-8">
            <div className="relative">
              <div className="h-20 w-20 rounded-full border-4 border-emerald-100 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {tr ? 'Ödeme İşleniyor...' : 'Processing Payment...'}
              </h3>
              <p className="text-sm text-gray-500">{tr ? 'Lütfen bekleyin' : 'Please wait'}</p>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full animate-pulse" style={{ width: '70%' }} />
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <Lock className="h-3 w-3" />
              {tr ? '256-bit SSL ile güvence altında' : 'Secured with 256-bit SSL'}
            </p>
          </div>
        )}

        {/* ── FORM ── */}
        {step === 'form' && (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0"
              style={{ background: 'linear-gradient(135deg,#1d4ed8,#1e40af)' }}>
              <div className="flex items-center gap-2.5">
                <ShoppingBag className="h-5 w-5 text-white" />
                <span className="font-bold text-white">{tr ? 'Siparişi Tamamla' : 'Complete Order'}</span>
              </div>
              <button onClick={onClose} className="text-white/70 hover:text-white rounded-full p-1 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Sipariş Özeti */}
              <div className="bg-slate-50 rounded-2xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  {tr ? 'Sipariş Özeti' : 'Order Summary'}
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {cart.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-600 truncate mr-2">
                        {item.product_name || item.title} × {item.quantity}
                      </span>
                      <span className="font-semibold text-gray-900 shrink-0">
                        {fmtCurrency(item.line_total || item.unit_price * item.quantity, item.currency)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between">
                  <span className="font-bold text-gray-800">{tr ? 'Toplam' : 'Total'}</span>
                  <span className="font-black text-blue-700 text-lg">{fmtCurrency(total, currency)}</span>
                </div>
              </div>

              {/* Firma Bilgileri */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  {tr ? 'Fatura Bilgileri' : 'Billing Info'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'company_name', label: tr ? 'Firma Adı' : 'Company Name', col: 2 },
                    { key: 'contact_name', label: tr ? 'Yetkili Ad Soyad' : 'Contact Name', col: 1 },
                    { key: 'email', label: 'E-posta', col: 1 },
                  ].map(({ key, label, col }) => (
                    <div key={key} style={{ gridColumn: `span ${col}` }}>
                      <label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <input
                        type={key === 'email' ? 'email' : 'text'}
                        value={company[key]}
                        onChange={e => setCompany(c => ({ ...c, [key]: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Kredi Kartı */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  {tr ? 'Ödeme Bilgileri' : 'Payment Details'}
                  <span className="ml-auto flex items-center gap-1 text-gray-400 font-normal normal-case tracking-normal">
                    <Lock className="h-3 w-3" /> SSL
                  </span>
                </p>

                {/* Kart görseli */}
                <div className="rounded-2xl p-5 mb-4 relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg,#1d4ed8 0%,#1e40af 50%,#1e3a8a 100%)', minHeight: 130 }}>
                  <div className="absolute top-3 right-4 text-white/30 font-black text-4xl tracking-wider">VISA</div>
                  <div className="absolute bottom-12 left-5">
                    <div className="w-8 h-6 rounded bg-yellow-300/60 mb-2" />
                  </div>
                  <div className="absolute bottom-4 left-5 right-5">
                    <p className="text-white font-mono text-lg tracking-widest font-bold mb-1">
                      {card.number || '•••• •••• •••• ••••'}
                    </p>
                    <div className="flex justify-between items-end">
                      <p className="text-white/80 text-xs uppercase tracking-wider">{card.holder || '—'}</p>
                      <p className="text-white/80 text-xs">{card.expiry || 'MM/YY'}</p>
                    </div>
                  </div>
                </div>

                {/* Kart formu */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{tr ? 'Kart Numarası' : 'Card Number'}</label>
                    <input
                      value={card.number}
                      onChange={e => setCard(c => ({ ...c, number: fmtCard(e.target.value) }))}
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid="card-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{tr ? 'Kart Sahibi' : 'Card Holder'}</label>
                    <input
                      value={card.holder}
                      onChange={e => setCard(c => ({ ...c, holder: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid="card-holder-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{tr ? 'Son Kullanma' : 'Expiry'}</label>
                      <input
                        value={card.expiry}
                        onChange={e => setCard(c => ({ ...c, expiry: fmtExpiry(e.target.value) }))}
                        placeholder="MM/YY"
                        maxLength={5}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                        data-testid="card-expiry-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">CVC/CVV</label>
                      <input
                        value={card.cvc}
                        onChange={e => setCard(c => ({ ...c, cvc: e.target.value.replace(/\D/g,'').slice(0,4) }))}
                        placeholder="•••"
                        maxLength={4}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                        data-testid="card-cvc-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Ödeme Butonları */}
              {/* Stripe — Gerçek ödeme */}
              <button
                onClick={handleStripePayment}
                disabled={cart.length === 0 || stripeLoading}
                className="w-full py-4 rounded-2xl text-white font-black text-base tracking-wide transition-all shadow-lg disabled:opacity-40 flex items-center justify-center gap-2.5"
                style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}
                data-testid="stripe-pay-btn"
              >
                {stripeLoading
                  ? <><Loader2 className="h-4 w-4 animate-spin" />{tr ? 'Stripe\'e Yönlendiriliyor...' : 'Redirecting to Stripe...'}</>
                  : <><ExternalLink className="h-4 w-4" />{tr ? `Stripe ile Öde · ${fmtCurrency(total, currency)}` : `Pay with Stripe · ${fmtCurrency(total, currency)}`}</>
                }
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">{tr ? 'veya test için' : 'or for testing'}</span></div>
              </div>

              {/* Mock — Test ödeme */}
              <button
                onClick={handlePay}
                disabled={cart.length === 0}
                className="w-full py-4 rounded-2xl text-white font-black text-base tracking-wide transition-all shadow-lg disabled:opacity-40 flex items-center justify-center gap-2.5"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
                data-testid="complete-order-btn"
              >
                <Lock className="h-4 w-4" />
                {tr ? `Mock Ödeme (Test) · ${fmtCurrency(total, currency)}` : `Mock Pay (Test) · ${fmtCurrency(total, currency)}`}
              </button>

              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                <Lock className="h-3 w-3" />
                {tr ? 'Bu işlem 256-bit SSL sertifikası ile korunmaktadır.' : 'This transaction is secured with 256-bit SSL.'}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
